import {
  MESSAGE_PAGE_SIZE,
  SOCKET_PATH,
  type ConversationDetail,
  type ConversationListItem,
  type CreatedUpload,
  type MessageItem,
  type Paginated,
  type SendMessageInput,
  type ServerToClientEvents,
  type UnreadMessages,
  type UploadInfo,
} from '@hemmafixa/shared';
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
  type QueryClient,
} from '@tanstack/react-query';
import { useEffect } from 'react';
import { io, type Socket } from 'socket.io-client';
import { api } from '@/lib/api-client';
import { env } from '@/lib/env';

// Conversations and messages (P5-03). Lists load over REST; a socket pushes new messages and read receipts and
// this file writes them into the query cache, so every screen updates without polling.

const conversationsKey = ['conversations'] as const;
const listKey = [...conversationsKey, 'list'] as const;
const unreadKey = [...conversationsKey, 'unread'] as const;
const messagesKey = (conversationId: string) => [...conversationsKey, 'messages', conversationId] as const;
const detailKey = (conversationId: string) => [...conversationsKey, 'detail', conversationId] as const;

export function useConversations() {
  return useQuery({
    queryKey: listKey,
    queryFn: () => api<Paginated<ConversationListItem>>('/conversations?pageSize=100'),
  });
}

export function useConversation(id: string) {
  return useQuery({
    queryKey: detailKey(id),
    queryFn: () => api<ConversationDetail>(`/conversations/${id}`),
  });
}

/** For the menu badge */
export function useUnreadMessages(enabled: boolean) {
  return useQuery({
    queryKey: unreadKey,
    queryFn: () => api<UnreadMessages>('/conversations/unread'),
    select: (result) => result.count,
    enabled,
    // The socket keeps it fresh; this covers a dropped connection
    refetchInterval: 5 * 60_000,
  });
}

type MessagePages = InfiniteData<MessageItem[], string | undefined>;

/** Newest page first; each page is newest first. fetchNextPage loads older messages. */
export function useMessages(conversationId: string) {
  return useInfiniteQuery({
    queryKey: messagesKey(conversationId),
    queryFn: ({ pageParam }) =>
      api<MessageItem[]>(
        `/messages?conversationId=${conversationId}${pageParam ? `&before=${encodeURIComponent(pageParam)}` : ''}`,
      ),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (page) => (page.length < MESSAGE_PAGE_SIZE ? undefined : page.at(-1)?.createdAt),
  });
}

/** Adds a message to its conversation's first page once, whether it came from the socket or the POST answer */
function addMessage(queryClient: QueryClient, message: MessageItem) {
  queryClient.setQueryData<MessagePages>(messagesKey(message.conversationId), (current) => {
    if (!current || current.pages.some((page) => page.some((other) => other.id === message.id)))
      return current;
    const [first = [], ...rest] = current.pages;
    return { ...current, pages: [[message, ...first], ...rest] };
  });
}

export function useSendMessage(conversationId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: Omit<SendMessageInput, 'conversationId'>) =>
      api<MessageItem>('/messages', { method: 'POST', body: { conversationId, ...input } }),
    onSuccess: (message) => {
      addMessage(queryClient, message);
      void queryClient.invalidateQueries({ queryKey: listKey });
    },
  });
}

export function useMarkRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (conversationId: string) =>
      api<void>(`/conversations/${conversationId}/read`, { method: 'POST' }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: listKey });
      void queryClient.invalidateQueries({ queryKey: unreadKey });
    },
  });
}

/** The steps useFileUploads needs for a file to send with a message */
export const messageFileSteps = {
  create: (file: File) =>
    api<CreatedUpload>('/uploads', {
      method: 'POST',
      body: { kind: 'message_file', fileName: file.name, contentType: file.type, sizeBytes: file.size },
    }),
  complete: (uploadId: string) => api<UploadInfo>(`/uploads/${uploadId}/complete`, { method: 'POST' }),
};

/**
 * One socket for the signed-in client or provider, while the app is open. It reconnects by itself; after a
 * reconnect the lists are reloaded in case something was missed meanwhile.
 */
export function useChatSocket(enabled: boolean) {
  const queryClient = useQueryClient();
  useEffect(() => {
    if (!enabled) return;
    const socket: Socket<ServerToClientEvents> = io(new URL(env.apiUrl).origin, {
      path: SOCKET_PATH,
      withCredentials: true,
      transports: ['websocket', 'polling'],
    });
    socket.on('message:new', (message) => {
      addMessage(queryClient, message);
      void queryClient.invalidateQueries({ queryKey: listKey });
      if (!message.fromMe) void queryClient.invalidateQueries({ queryKey: unreadKey });
    });
    socket.on('conversation:read', () => {
      void queryClient.invalidateQueries({ queryKey: listKey });
    });
    socket.io.on('reconnect', () => void queryClient.invalidateQueries({ queryKey: conversationsKey }));
    // The server refuses an expired access cookie; by the next try the app has usually refreshed it
    let retry: ReturnType<typeof setTimeout> | undefined;
    socket.on('disconnect', (reason) => {
      if (reason === 'io server disconnect') retry = setTimeout(() => socket.connect(), 30_000);
    });
    return () => {
      clearTimeout(retry);
      socket.disconnect();
    };
  }, [enabled, queryClient]);
}
